import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
} from "@mui/material";
import {
    Add as AddIcon,
    Search,
    Info,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close,
} from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import useNotification from "../common/Notification";
import {
    listEmailDomains,
    listSenderEmails,
    createSenderEmail,
    updateSenderEmail,
    deleteSenderEmail,
    isDomainVerified,
} from "../../api/emailBusinessApi";

const GLASS_PAPER_SX = {
    background: "rgba(255, 255, 255, 0.6)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.85)",
    borderRadius: 2,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
};

const ROWS_PER_PAGE = 10;
const EMPTY_FORM = { email_domain_id: "", local_part: "", reply_to: "", name: "" };

const splitEmail = (email) => {
    const value = String(email || "").trim().toLowerCase();
    const atIndex = value.lastIndexOf("@");
    if (atIndex < 1 || atIndex === value.length - 1) {
        return { local_part: value, domain: "" };
    }
    return {
        local_part: value.slice(0, atIndex),
        domain: value.slice(atIndex + 1),
    };
};


const mapSender = (item) => ({
    id: item.id,
    email_domain_id: item.email_domain_id,
    name: item.name || item.sender_email_id || item.email || "",
    email: item.sender_email_id || item.email || "",
    reply_to: item.reply_to || "",
    subtitle: item.reply_to
        ? `Reply-to: ${item.reply_to}`
        : item.name || item.description || "",
    status: item.status || "Pending",
});

const fieldSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        backgroundColor: "rgba(255,255,255,0.8)",
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.main",
            borderWidth: 2,
        },
    },
};

const ManageEmailSenders = () => {
    const notify = useNotification();
    const [senders, setSenders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [verifiedDomains, setVerifiedDomains] = useState([]);

    const isEditing = editingId != null;

    const fetchSenders = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await listSenderEmails({ per_page: 100 });
            setSenders(rows.map(mapSender));
        } catch (error) {
            notify(error.message || "Failed to load sender emails", "error");
            setSenders([]);
        } finally {
            setLoading(false);
        }
    }, [notify]);

    const fetchVerifiedDomains = useCallback(async () => {
        try {
            const rows = await listEmailDomains({ per_page: 100 });
            setVerifiedDomains(
                (Array.isArray(rows) ? rows : [])
                    .filter(isDomainVerified)
                    .map((item) => ({
                        id: item.id,
                        domain: String(item.domain || "").trim().toLowerCase(),
                    }))
            );
        } catch (error) {
            setVerifiedDomains([]);
            notify(error.message || "Failed to load verified domains", "error");
        }
    }, [notify]);

    useEffect(() => {
        fetchSenders();
        fetchVerifiedDomains();
    }, [fetchSenders, fetchVerifiedDomains]);

    useEffect(() => {
        if (!dialogOpen || !verifiedDomains.length) return;
        setForm((prev) => {
            if (prev.email_domain_id) return prev;
            return { ...prev, email_domain_id: String(verifiedDomains[0].id) };
        });
    }, [verifiedDomains, dialogOpen]);

    const selectedDomain = verifiedDomains.find(
        (d) => String(d.id) === String(form.email_domain_id)
    );

    const senderEmailPreview = selectedDomain && form.local_part.trim()
        ? `${form.local_part.trim().toLowerCase()}@${selectedDomain.domain}`
        : "";

    const canSubmitSender =
        Boolean(form.local_part.trim()) &&
        Boolean(form.email_domain_id) &&
        verifiedDomains.some((d) => String(d.id) === String(form.email_domain_id));

    const filteredSenders = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return senders;
        return senders.filter(
            (s) =>
                (s.name || "").toLowerCase().includes(q) ||
                (s.email || "").toLowerCase().includes(q) ||
                (s.status || "").toLowerCase().includes(q) ||
                (s.subtitle || "").toLowerCase().includes(q) ||
                (s.reply_to || "").toLowerCase().includes(q)
        );
    }, [query, senders]);

    const totalPages = Math.max(1, Math.ceil(filteredSenders.length / ROWS_PER_PAGE));
    const paginatedSenders = filteredSenders.slice(
        (page - 1) * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE
    );

    const openCreateDialog = () => {
        setEditingId(null);
        setForm({
            ...EMPTY_FORM,
            email_domain_id: verifiedDomains[0]?.id ? String(verifiedDomains[0].id) : "",
        });
        setDialogOpen(true);
        fetchVerifiedDomains();
    };

    const openEditDialog = (sender) => {
        const email = sender.email || sender.sender_email_id || "";
        const { local_part } = splitEmail(email);
        setEditingId(sender.id);
        setForm({
            email_domain_id: sender.email_domain_id ? String(sender.email_domain_id) : "",
            local_part,
            reply_to: sender.reply_to || "",
            name: sender.name || "",
        });
        setDialogOpen(true);
        fetchVerifiedDomains();
    };

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const handleFormChange = (field) => (e) => {
        const value = e.target.value;
        setForm((prev) => ({
            ...prev,
            [field]:
                field === "local_part"
                    ? value.replace(/@/g, "").replace(/\s/g, "")
                    : value,
        }));
    };

    const handleSaveSender = async () => {
        const domain = verifiedDomains.find(
            (d) => String(d.id) === String(form.email_domain_id)
        );
        const senderEmailId = domain
            ? `${form.local_part.trim().toLowerCase()}@${domain.domain}`
            : "";
        const replyTo = form.reply_to.trim().toLowerCase();
        if (!senderEmailId || saving) return;

        if (!domain) {
            notify(
                verifiedDomains.length === 0
                    ? "No verified domains available. Verify a domain under Manage Domains before creating a sender."
                    : "Please select a verified domain",
                "error"
            );
            return;
        }

        setSaving(true);
        try {
            const payload = {
                email_domain_id: domain.id,
                sender_email_id: senderEmailId,
                reply_to: replyTo || undefined,
                name: form.name.trim() || undefined,
            };

            if (isEditing) {
                await updateSenderEmail(editingId, {
                    reply_to: replyTo || undefined,
                    name: form.name.trim() || undefined,
                });
                notify("Sender email updated successfully", "success");
            } else {
                await createSenderEmail(payload);
                setPage(1);
                notify("Sender email created successfully", "success");
            }

            setDialogOpen(false);
            setEditingId(null);
            setForm(EMPTY_FORM);
            await fetchSenders();
        } catch (error) {
            notify(
                error.message ||
                    (isEditing ? "Failed to update sender email" : "Failed to create sender email"),
                "error"
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteSenderEmail(id);
            notify("Sender email deleted successfully", "success");
            await fetchSenders();
        } catch (error) {
            notify(error.message || "Failed to delete sender email", "error");
        }
    };

    return (
        <Box sx={{ ...WB_SX.page, p: 0 }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "flex-end" },
                    gap: 2,
                    mb: 2.5,
                }}
            >
                <Box sx={{ maxWidth: { xs: "100%", md: "760px" } }}>
                    <Typography
                        variant="h5"
                        sx={{
                            ...WB_UI.typography.pageTitle,
                            color: "text.primary",
                        }}
                    >
                        Manage Email Senders
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                        Manage sender identities used for business email campaigns.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Chip
                        size="small"
                        label={`Total: ${senders.length}`}
                        sx={{
                            fontWeight: 700,
                            borderRadius: 2,
                            bgcolor: "rgba(25,118,210,0.10)",
                            color: "text.primary",
                            border: "1px solid rgba(25,118,210,0.20)",
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreateDialog}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                        }}
                    >
                        Create Sender
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ ...GLASS_PAPER_SX, mb: 2 }}>
                <Box
                    sx={{
                        p: 2,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1.5,
                        alignItems: "center",
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search sender name or email…"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setPage(1);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={fieldSx}
                    />
                    <Chip
                        size="small"
                        label={`Showing: ${filteredSenders.length}`}
                        sx={{
                            fontWeight: 700,
                            borderRadius: 2,
                            bgcolor: "rgba(0,0,0,0.04)",
                            border: "1px solid rgba(0,0,0,0.08)",
                        }}
                    />
                </Box>
            </Paper>

            <Alert
                severity="info"
                icon={<Info fontSize="small" />}
                sx={{
                    mb: 2,
                    borderRadius: 2,
                    ...GLASS_PAPER_SX,
                    border: "1px solid rgba(25,118,210,0.18)",
                    background: "rgba(25,118,210,0.06)",
                    "& .MuiAlert-message": { fontWeight: 600 },
                }}
            >
                Sender emails can only be created for verified domains.
            </Alert>

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    ...GLASS_PAPER_SX,
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                            {[
                                { label: "Sender Name", align: "left" },
                                { label: "Email Address", align: "left" },
                                { label: "Actions", align: "center" },
                            ].map((h) => (
                                <TableCell
                                    key={h.label}
                                    align={h.align}
                                    sx={{
                                        fontWeight: 800,
                                        color: "text.primary",
                                        bgcolor: "#f8fafc",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        py: 1.5,
                                    }}
                                >
                                    {h.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : paginatedSenders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {query
                                            ? "No senders found matching your search"
                                            : "No senders found"}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedSenders.map((sender) => (
                                <TableRow
                                    key={sender.id}
                                    hover
                                    sx={{
                                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.02)" },
                                        "& td": {
                                            borderBottom: "1px solid",
                                            borderColor: "divider",
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {sender.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                        >
                                            {sender.subtitle}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{sender.email}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                justifyContent: "center",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => openEditDialog(sender)}
                                                sx={{
                                                    minWidth: "auto",
                                                    px: 1.25,
                                                    borderRadius: 1.5,
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    borderColor: "divider",
                                                    "&:hover": {
                                                        borderColor: "primary.main",
                                                        backgroundColor: "rgba(25,118,210,0.06)",
                                                    },
                                                }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="text"
                                                startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => handleDelete(sender.id)}
                                                sx={{
                                                    minWidth: "auto",
                                                    px: 1.25,
                                                    borderRadius: 1.5,
                                                    textTransform: "none",
                                                    fontWeight: 800,
                                                    color: "error.main",
                                                    "&:hover": {
                                                        backgroundColor: "rgba(211,47,47,0.06)",
                                                    },
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredSenders.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mt: 2,
                    }}
                >
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                        Showing{" "}
                        {(page - 1) * ROWS_PER_PAGE + 1} to{" "}
                        {Math.min(page * ROWS_PER_PAGE, filteredSenders.length)} of{" "}
                        {filteredSenders.length} senders
                    </Typography>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        shape="rounded"
                        size="small"
                    />
                </Box>
            )}

            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: "rgba(0,0,0,0.45)" },
                    },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        backgroundColor: "background.paper",
                        boxShadow: 6,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                    }}
                >
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {isEditing ? "Edit Sender" : "Add Sender"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {isEditing
                                ? "Update the sender and reply-to addresses for this identity."
                                : "Add a sender email and reply-to address for business campaigns."}
                        </Typography>
                    </Box>
                    <Button
                        onClick={closeDialog}
                        disabled={saving}
                        sx={{ minWidth: "auto", p: 0.5, alignSelf: "flex-start" }}
                        color="inherit"
                    >
                        <Close />
                    </Button>
                </DialogTitle>

                <DialogContent sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
                    {verifiedDomains.length === 0 && (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            No verified domains found. Verify a domain under Manage Domains before creating a sender email.
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Sender Email{" "}
                            <Box component="span" sx={{ color: "error.main" }}>*</Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.25 }}>
                            Enter the local part and select a verified domain.
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                            <TextField
                                autoFocus
                                size="small"
                                placeholder="sender"
                                value={form.local_part}
                                onChange={handleFormChange("local_part")}
                                disabled={saving || verifiedDomains.length === 0 || isEditing}
                                sx={{ ...fieldSx, flex: 1, minWidth: 140 }}
                            />
                            <Typography variant="body1" sx={{ mt: 1, fontWeight: 700, color: "text.secondary", flexShrink: 0 }}>@</Typography>
                            <FormControl size="small" sx={{ ...fieldSx, flex: 1.2, minWidth: 160 }} disabled={saving || verifiedDomains.length === 0 || isEditing}>
                                <InputLabel id="verified-domain-label">Domain</InputLabel>
                                <Select
                                    labelId="verified-domain-label"
                                    label="Domain"
                                    value={form.email_domain_id}
                                    onChange={handleFormChange("email_domain_id")}
                                >
                                    {verifiedDomains.map((domain) => (
                                        <MenuItem key={domain.id} value={String(domain.id)}>
                                            {domain.domain}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {senderEmailPreview && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                Preview: {senderEmailPreview}
                            </Typography>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Display name
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Optional display name"
                            value={form.name}
                            onChange={handleFormChange("name")}
                            disabled={saving}
                            sx={fieldSx}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Reply-To Email
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1.25 }}
                        >
                            Replies from recipients will be sent to this address (optional).
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            type="email"
                            placeholder="Enter reply-to email"
                            value={form.reply_to}
                            onChange={handleFormChange("reply_to")}
                            disabled={saving}
                            sx={fieldSx}
                        />
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 2.5,
                        pt: 1,
                        borderTop: "1px solid rgba(0,0,0,0.08)",
                        justifyContent: "space-between",
                    }}
                >
                    <Button
                        onClick={closeDialog}
                        disabled={saving}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            color: "primary.main",
                            px: 0,
                            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveSender}
                        disabled={!canSubmitSender || saving}
                        startIcon={
                            saving ? <CircularProgress size={16} color="inherit" /> : null
                        }
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                            px: 3,
                        }}
                    >
                        {saving
                            ? "Saving..."
                            : isEditing
                              ? "Save Changes"
                              : "Add Sender"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ManageEmailSenders;
